import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Dismiss only when press AND release both land on the overlay itself.

A click is one event on the common ancestor of mousedown and mouseup. So
`onClick={onClose}` on the backdrop closes the dialog when the operator
presses inside a menu/panel and releases on the dimmed area — the menu
vanishes mid-gesture. Pointer down+up on the backdrop only matches a
real "clicked the outside".
*/
export function useBackdropDismiss(onClose: () => void, enabled = true) {
  const downOnBackdrop = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    // Bubble, not capture: open menus (Select / SearchableSelect) listen in
    // capture and stop, so the first Escape closes the menu and the next
    // closes this overlay.
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, enabled]);

  return {
    onPointerDown: (e: React.PointerEvent) => {
      downOnBackdrop.current = e.target === e.currentTarget;
    },
    onPointerUp: (e: React.PointerEvent) => {
      const upOnBackdrop = e.target === e.currentTarget;
      if (downOnBackdrop.current && upOnBackdrop) onClose();
      downOnBackdrop.current = false;
    },
  };
}

// Dialogs MUST portal to <body>.
//
// `.bf-card` applies `backdrop-blur-sm` -> `backdrop-filter: blur()`, and an
// element with a backdrop-filter becomes the CONTAINING BLOCK and a stacking
// context for its `position: fixed` descendants. A dialog rendered inside a
// card was therefore neither viewport-sized nor above the rest of the page:
// its `inset-0` resolved against the card and its `z-40` only ranked it within
// that card, so sibling content painted on top and swallowed clicks aimed at
// the dialog. Portaling escapes every ancestor stacking context, which no
// amount of z-index tuning at the call site can do.
//
// The panel is deliberately OPAQUE (`bg-bf-surface`, not the `/70` that
// `.bf-card` uses) so page content cannot be read through a modal.

export function Modal({
  onClose,
  children,
  labelledBy,
  panelClassName = "w-full max-w-md",
}: {
  onClose: () => void;
  children: ReactNode;
  labelledBy?: string;
  panelClassName?: string;
}) {
  useEffect(() => {
    // Prevent the page behind the dialog from scrolling under the overlay.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const backdrop = useBackdropDismiss(onClose);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      {...backdrop}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={
          "max-h-[90vh] overflow-y-auto rounded-xl border border-white/10 " +
          "bg-bf-surface p-4 shadow-2xl shadow-black/50 " +
          panelClassName
        }
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
